<template>
  <div id="planningDashboard">
    <div v-if="school.site_code !== null">
    <Header
      pageIcon="laptop"
      :header="header"
      :content="content"
      :sitecode="school.site_code"
    />

    <v-container
    v-if="school.site_code !== null"
    fluid
    style="margin-top: -24px;" class="contentSpacer"
  >

    <v-row justify="space-between">
      <v-col
        cols="12"
        lg="4"
      >
        <v-tabs
          slider-color="accent"
          color="accent"
          background-color="transparent"
        >
          <v-tab @click="showCharts">CHARTS</v-tab>
          <v-tab @click="showDevices">DEVICES</v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <DevicesGlobalFilter/>

    <div v-show="isCharts">
      <v-row>
        <v-col
          cols="12"
          lg="6"
          id="ictp-hc-form-factor"
        >
          <FormFactor
            class="shadowbox"
            :sitecode="school.site_code"
            :loading="deviceCountByFormFactor.loading"
            :error="deviceCountByFormFactor.error"
            :lastUpdatedAt="deviceCountByFormFactor.lastUpdatedAt"
          />
        </v-col>

        <v-col
          cols="12"
          lg="6"
          id="ictp-hc-model-count"
        >
          <ModelCount
            class="shadowbox"
            :sitecode="school.site_code"
            :loading="deviceCountByModel.loading"
            :error="deviceCountByModel.error"
            :lastUpdatedAt="deviceCountByModel.lastUpdatedAt"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          lg="4"
          id="ictp-hc-device-age"
        >
          <DeviceAge
            class="shadowbox"
            :sitecode="school.site_code"
            :loading="devicesByAge.loading"
            :error="devicesByAge.error"
            :lastUpdatedAt="devicesByAge.lastUpdatedAt"
          />
        </v-col>

        <v-col
          cols="12"
          lg="4"
          id="ictp-hc-utilisation"
        >
          <Utilisation
            class="shadowbox"
            :sitecode="school.site_code"
            :loading="devicesByUtilisation.loading"
            :error="devicesByUtilisation.error"
            :lastUpdatedAt="devicesByUtilisation.lastUpdatedAt"
          />
        </v-col>

        <v-col
          cols="12"
          lg="4"
          id="ictp-hc-warranty"
        >
          <Warranty
            class="shadowbox"
            :sitecode="school.site_code"
            :loading="deviceCountByWarrantyExpiration.loading"
            :error="deviceCountByWarrantyExpiration.error"
            :lastUpdatedAt="deviceCountByWarrantyExpiration.lastUpdatedAt"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          lg="6"
          id="ictp-hc-10-build-number"
        >
          <TopTenBuildNumber
            class="shadowbox"
            :sitecode="school.site_code"
            :loading="deviceCountByModel.loading"
            :error="deviceCountByModel.error"
            :lastUpdatedAt="deviceCountByModel.lastUpdatedAt"
          />
        </v-col>

        <v-col
          cols="12"
          lg="6"
          id="ictp-hc-10-operating-system"
        >
          <TopTenOperatingSystem
            class="shadowbox"
            :sitecode="school.site_code"
            :loading="deviceCountByModel.loading"
            :error="deviceCountByModel.error"
            :lastUpdatedAt="deviceCountByModel.lastUpdatedAt"
          />
        </v-col>
      </v-row>
    </div>

    <v-row v-show="!isCharts">
      <v-col id="ictp-tbl-devices">
        <DevicesTable
          :sitecode="school.site_code"
          :loading="devices.loading"
          :error="devices.error"
          :filters="filters"
        />
      </v-col>
    </v-row>
  </v-container>

    <v-alert
      v-else
      :value="true"
      color="warning"
      icon="priority_high"
      outlined
    >
      Please select a school from the school picker in the top right to display data for this page. Simply click "Search
      for a school", then start typing the name of your desired school.
    </v-alert>
    </div>
    <div v-else>
      <EmptyState/>
    </div>
  </div>
</template>

<script>
  import {mapActions, mapState, mapGetters} from "vuex";
  import DevicesTable from "../DevicesTable";
  import FormFactor from "../highcharts/FormFactor";
  import ModelCount from "../highcharts/ModelCount";
  import Warranty from "../highcharts/Warranty";
  import DeviceAge from "../highcharts/DeviceAge";
  import Utilisation from "../highcharts/Utilisation";
  import DevicesGlobalFilter from "../DevicesGlobalFilter";
  import TopTenOperatingSystem from "../highcharts/TopTenOperatingSystem"
  import TopTenBuildNumber from "../highcharts/TopTenBuildNumber";
  import Header from "../simpleComponents/Header";
  import EmptyState from "../simpleComponents/EmptyState";

  export default {
    name: "PlanningDashboard",
    components: {
      DevicesGlobalFilter,
      FormFactor,
      ModelCount,
      Warranty,
      DeviceAge,
      DevicesTable,
      Utilisation,
      TopTenBuildNumber,
      TopTenOperatingSystem,
      Header,
      EmptyState
    },
    data() {
      return {
        isCharts: true,
        header: "Managed Devices",
        content: "This page provides insight to your school's Managed Devices (for Google, Microsoft and Apple devices, excluding iOS devices such as iPads and iPhones), pulling data from multiple existing databases to allow you to see key information regarding the devices in one portal." +
            "<br/><br/>" +
            "The <i>Devices</i> tab shows a table of all of the data available regarding Managed Devices in your school, and the Charts tab shows visualisations based on the data in that table. " +
            "<br/><br/>" +
            "For information on where this data comes from and how often it is updated, visit our <i>Help & Support</i> page."
      }
    },
    methods: {
      ...mapActions(['fetchDeviceCount']),
      showCharts() {
        this.sendEventToGA('show Charts click');
        this.isCharts = true;
      },
      showDevices() {
        this.sendEventToGA('show Devices click');
        this.isCharts = false;
      },
      sendEventToGA(actionName) {
        this.$ga.event({
          eventCategory: 'Planning Dashboard',
          eventAction: `${actionName}`,
          eventLabel: `sitecode:${this.school.site_code}`
        });
      }
    },
    computed: {
      ...mapGetters([
        "claims",
        "deviceCount",
        "devicesPerStudent",
      ]),
      ...mapState({
        school: s => s.school.selected,
        filters: s => s.api.filters,
        devicesByAge: s => s.api.chartData.devicesByAge,
        deviceCountByModel: s => s.api.chartData.deviceCountByModel,
        deviceCountByFormFactor: s => s.api.chartData.deviceCountByFormFactor,
        deviceCountByWarrantyExpiration: s => s.api.chartData.deviceCountByWarrantyExpiration,
        devicesByUtilisation: s => s.api.chartData.devicesByUtilisation,
        devices: s => s.api.devices,
      }),
      fontSize() {
        switch (this.$vuetify.breakpoint.name) {
          case 'xl':
          case 'lg':
          case 'md':
            return {fontSize: '24px', lineHeight: '36px', display: 'block'};
          case 'sm':
          case 'xs':
            return {fontSize: '21px', lineHeight: '36px', display: 'block'};
        }
        return {fontSize: '24px', lineHeight: '36px', display: 'block'};
      }
    },
  };
</script>

<style scoped>
  .shadowbox {
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
    margin-bottom: 1.4rem;
  }

  .theme--dark.v-expansion-panels .v-expansion-panel {
    background-color: #0E2D58 !important;
  }
</style>
